import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import VenueGalleryManager from './VenueGalleryManager';
import VenueFeaturesManager from './VenueFeaturesManager';
import VenueBlogsManager from './VenueBlogsManager';
import VenueMenuManager from './VenueMenuManager';

const VenueDetailLayer = ({ venueId }) => {
    const { auth, user } = useAuth();
    const navigate = useNavigate();
    const [venue, setVenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedVouchers, setSelectedVouchers] = useState([]);
    const [savingVouchers, setSavingVouchers] = useState(false);

    const fetchVenueData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`);
        
            if (res.ok) {
                const venueData = await res.json();
                setVenue(venueData);

                if (Array.isArray(venueData.vouchers)) {
                    setSelectedVouchers(venueData.vouchers);
                } else if (venueData.vouchers) {
                    try {
                        const parsed = typeof venueData.vouchers === 'string'
                            ? JSON.parse(venueData.vouchers)
                            : venueData.vouchers;
                        setSelectedVouchers(Array.isArray(parsed) ? parsed : []);
                    } catch (e) {
                        setSelectedVouchers([]);
                    }
                } else {
                    setSelectedVouchers([]);
                }
            } else {
                toast.error('Venue not found');
                navigate('/my-venues');
            }
        } catch (error) {
            console.error('Error fetching venue:', error);
            toast.error('Failed to load venue');
        } finally {
            setLoading(false);
        }
    }, [auth, venueId, navigate]);

    useEffect(() => {
        if (venueId) {
            fetchVenueData();
        }
    }, [venueId, fetchVenueData]);

    const getStatusBadge = (status) => {
        const statusMap = {
            pending: { bg: 'bg-warning-focus', text: 'text-warning-main', label: 'Pending Review' },
            approved: { bg: 'bg-success-focus', text: 'text-success-main', label: 'Approved' },
            rejected: { bg: 'bg-danger-focus', text: 'text-danger-main', label: 'Rejected' },
            suspended: { bg: 'bg-secondary-focus', text: 'text-secondary-main', label: 'Suspended' }
        };
        const s = statusMap[status] || statusMap.pending;
        return (
            <span className={`${s.bg} ${s.text} px-20 py-10 radius-8 fw-semibold text-md`}>
                {s.label}
            </span>
        );
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: 'mdi:information-outline' },
        { id: 'gallery', label: 'Gallery', icon: 'mdi:image-multiple' },
        { id: 'features', label: 'Features', icon: 'mdi:star-outline' },
        { id: 'blogs', label: 'Blog Posts', icon: 'mdi:post-outline' },
        { id: 'menu', label: 'Menu', icon: 'mdi:food-outline' },
        { id: 'vouchers', label: 'Vouchers & Deals', icon: 'mdi:ticket-percent-outline' }
    ];

    const vouchers = [
        {
            id: 'fazaa',
            name: 'Fazaa',
            image: '/assets/images/vouchers/fazaa.png',
            url: 'https://www.fazaa.ae/'
        },
        {
            id: 'esaad',
            name: 'Esaad',
            image: '/assets/images/vouchers/esaad.png',
            url: 'https://esaad.dubaipolice.gov.ae/home'
        },
        {
            id: 'entertainer',
            name: 'The ENTERTAINER',
            image: '/assets/images/vouchers/theentertainerme.png',
            url: 'https://www.theentertainerme.com/'
        }
    ];

    const toggleVoucher = (id) => {
        setSelectedVouchers((prev) =>
            prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
        );
    };

    const handleSaveVouchers = useCallback(async () => {
        if (!venueId) return;
        setSavingVouchers(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ vouchers: selectedVouchers })
            });

            let data = {};
            try {
                data = await response.json();
            } catch (e) {
                data = {};
            }

            if (response.ok) {
                toast.success('Vouchers updated successfully');
            } else {
                toast.error(data.message || 'Failed to update vouchers');
            }
        } catch (error) {
            console.error('Error updating vouchers:', error);
            toast.error('Failed to update vouchers');
        } finally {
            setSavingVouchers(false);
        }
    }, [auth, venueId, selectedVouchers]);

    if (loading) {
        return (
            <div className="card h-100 p-0 radius-12">
                <div className="card-body p-24 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!venue) {
        return (
            <div className="card h-100 p-0 radius-12">
                <div className="card-body p-24 text-center">
                    <Icon icon="mdi:store-off" className="text-secondary-light mb-16" style={{ fontSize: '64px' }} />
                    <h5 className="text-secondary-light">Venue not found</h5>
                    <Link to="/my-venues" className="btn btn-primary-600 mt-16">
                        Back to My Venues
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Venue Header Card */}
            <div className="card mb-16">
                <div className="card-body p-24">
                    <div className="row align-items-center">
                        <div className="col-auto">
                            {venue.logo_url ? (
                                <img 
                                    src={venue.logo_url} 
                                    alt={venue.title}
                                    className="w-100-px h-100-px rounded-16 object-fit-cover"
                                />
                            ) : (
                                <div className="w-100-px h-100-px rounded-16 bg-primary-focus d-flex align-items-center justify-content-center">
                                    <Icon icon="mdi:store" className="text-primary-main" style={{ fontSize: '48px' }} />
                                </div>
                            )}
                        </div>
                        <div className="col">
                            <div className="d-flex align-items-center gap-12 mb-8">
                                <h4 className="mb-0 fw-bold">{venue.title || venue.business_name}</h4>
                                {getStatusBadge(venue.verification_status || 'pending')}
                            </div>
                            <p className="text-secondary-light mb-8">
                                <Icon icon="mdi:map-marker" className="me-4" />
                                {venue.address_street && `${venue.address_street}, `}
                                {venue.address_city && `${venue.address_city}, `}
                                {venue.address_country || venue.address || 'No address provided'}
                            </p>
                            <div className="d-flex gap-24">
                                <div className="d-flex align-items-center gap-8">
                                    <Icon icon="mdi:image-multiple" className="text-primary-600" />
                                    <span className="text-sm">{venue.gallery_count || 0} images</span>
                                </div>
                                <div className="d-flex align-items-center gap-8">
                                    <Icon icon="mdi:star" className="text-warning-main" />
                                    <span className="text-sm">{venue.feature_count || 0} features</span>
                                </div>
                                <div className="d-flex align-items-center gap-8">
                                    <Icon icon="mdi:post" className="text-info-main" />
                                    <span className="text-sm">{venue.blog_count || 0} posts</span>
                                </div>
                            </div>
                        </div>
                        <div className="col-auto">
                            <div className="d-flex align-items-center gap-12">
                                <button
                                    className="btn btn-outline-primary-600 d-flex align-items-center justify-content-center"
                                    onClick={() => navigate(`/edit-venue/${venue.id}`)}
                                >
                                    <Icon icon="lucide:edit" className="me-8 text-xl" />
                                    Edit Venue
                                </button>
                                <Link to="/my-venues" className="btn btn-secondary-600 d-flex align-items-center justify-content-center">
                                    <Icon icon="mdi:arrow-left" className="me-8 text-xl" />
                                    Back
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="card mb-16">
                <div className="card-body p-0">
                    <ul className="nav nav-tabs border-0 px-24" role="tablist">
                        {tabs.map((tab) => (
                            <li className="nav-item me-24" key={tab.id}>
                                <button
                                    className={`nav-link d-flex align-items-center gap-8 border-0 ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                    type="button"
                                >
                                    <Icon icon={tab.icon} />
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
                <div className="row g-24">
                    {/* Description */}
                    <div className="col-lg-8">
                        <div className="card h-100">
                            <div className="card-header">
                                <h6 className="mb-0">About This Venue</h6>
                            </div>
                            <div className="card-body">
                                <p className="text-secondary-light">
                                    {venue.description || 'No description provided.'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Quick Info */}
                    <div className="col-lg-4">
                        <div className="card h-100">
                            <div className="card-header">
                                <h6 className="mb-0">Quick Info</h6>
                            </div>
                            <div className="card-body">
                                <ul className="list-unstyled mb-0">
                                    <li className="d-flex justify-content-between py-8 border-bottom">
                                        <span className="text-secondary-light">Created</span>
                                        <span className="fw-medium">
                                            {venue.created_at ? new Date(venue.created_at).toLocaleDateString() : 'N/A'}
                                        </span>
                                    </li>
                                    <li className="d-flex justify-content-between py-8 border-bottom">
                                        <span className="text-secondary-light">Last Updated</span>
                                        <span className="fw-medium">
                                            {venue.updated_at ? new Date(venue.updated_at).toLocaleDateString() : 'N/A'}
                                        </span>
                                    </li>
                                    {venue.license_number && (
                                        <li className="d-flex justify-content-between py-8 border-bottom">
                                            <span className="text-secondary-light">License</span>
                                            <span className="fw-medium">{venue.license_number}</span>
                                        </li>
                                    )}
                                    {venue.phone && (
                                        <li className="d-flex justify-content-between py-8 border-bottom">
                                            <span className="text-secondary-light">Phone</span>
                                            <span className="fw-medium">{venue.phone}</span>
                                        </li>
                                    )}
                                    {venue.email && (
                                        <li className="d-flex justify-content-between py-8">
                                            <span className="text-secondary-light">Email</span>
                                            <span className="fw-medium">{venue.email}</span>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Address Details */}
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">
                                    <Icon icon="mdi:map-marker" className="me-8 text-primary-600" />
                                    Location Details
                                </h6>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-16">
                                            <label className="text-secondary-light text-sm">Street Address</label>
                                            <p className="fw-medium mb-0">{venue.address_street || venue.address || 'Not provided'}</p>
                                        </div>
                                        <div className="mb-16">
                                            <label className="text-secondary-light text-sm">City</label>
                                            <p className="fw-medium mb-0">{venue.address_city || 'Not provided'}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-16">
                                            <label className="text-secondary-light text-sm">State/Province</label>
                                            <p className="fw-medium mb-0">{venue.address_state || 'Not provided'}</p>
                                        </div>
                                        <div className="mb-16">
                                            <label className="text-secondary-light text-sm">Country</label>
                                            <p className="fw-medium mb-0">{venue.address_country || 'Not provided'}</p>
                                        </div>
                                    </div>
                                </div>
                                {venue.location && (
                                    <div className="mt-16 p-16 bg-neutral-50 radius-8">
                                        <label className="text-secondary-light text-sm d-block mb-8">Coordinates</label>
                                        <code>
                                            Lat: {venue.location?.coordinates?.[1] || venue.latitude || 'N/A'}, 
                                            Lng: {venue.location?.coordinates?.[0] || venue.longitude || 'N/A'}
                                        </code>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'gallery' && (
                <VenueGalleryManager venueId={venueId} />
            )}

            {activeTab === 'features' && (
                    <VenueFeaturesManager venueId={venueId} />
                )}

                {activeTab === 'blogs' && (
                    <VenueBlogsManager 
                        venueId={venueId} 
                        isOwner={user && venue && user.id === venue.owner_id}
                    />
                )}

                {activeTab === 'menu' && (
                <VenueMenuManager venueId={venueId} />
            )}

            {activeTab === 'vouchers' && (
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">Vouchers & Deals</h6>
                        <div className="d-flex align-items-center gap-12">
                            <span className="text-secondary-light text-sm">
                                Hover over a card to preview the partner link.
                            </span>
                            <button
                                type="button"
                                className="btn btn-primary-600 btn-sm"
                                onClick={handleSaveVouchers}
                                disabled={savingVouchers}
                            >
                                {savingVouchers ? 'Saving...' : 'Save Selection'}
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="row g-24">
                            {vouchers.map((voucher) => (
                                <div className="col-md-4" key={voucher.id}>
                                    <label
                                        className={`d-block border radius-12 p-16 h-100 cursor-pointer ${
                                            selectedVouchers.includes(voucher.id)
                                                ? 'border-primary-600 bg-primary-50'
                                                : 'border-neutral-200'
                                        }`}
                                        title={voucher.url}
                                    >
                                        <div className="d-flex justify-content-between align-items-start mb-12">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={selectedVouchers.includes(voucher.id)}
                                                    onChange={() => toggleVoucher(voucher.id)}
                                                />
                                                <span className="ms-8 fw-medium">{voucher.name}</span>
                                            </div>
                                        </div>
                                        <div className="text-center mb-12">
                                            <img
                                                src={voucher.image}
                                                alt={voucher.name}
                                                className="img-fluid"
                                                style={{ maxHeight: '80px', objectFit: 'contain' }}
                                            />
                                        </div>
                                        <div className="text-center">
                                            <a
                                                href={voucher.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary-600 text-sm"
                                            >
                                                {voucher.url}
                                            </a>
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default VenueDetailLayer;
